import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Plus, Trash2, X, Eye, EyeOff, RefreshCw, Users, Pencil } from 'lucide-react';
import SectionDivider from '../../components/shared/SectionDivider';

/* ── Types ──────────────────────────────────────────────────────────────── */
interface AdminUser {
  id:          string;
  username:    string;
  displayName: string;
  role:        string;
  org:         string;
  scope:       string;
  createdAt:   string;
}

interface AddUserResponse {
  success:    boolean;
  user:       AdminUser;
  totpSecret: string;
  otpauthUrl: string;
  qrCode:     string;
}

const ROLE_OPTIONS = [
  { value: 'national',  label: 'National / MNRE Admin' },
  { value: 'state',     label: 'State Agency' },
  { value: 'discom',    label: 'DISCOM Admin' },
  { value: 'consumer',  label: 'Consumer' },
  { value: 'vendor',    label: 'Vendor / EPC' },
  { value: 'admin',     label: 'Administrator' },
];

const ROLE_BADGE: Record<string, string> = {
  admin:    'bg-purple-100 text-purple-700',
  national: 'bg-blue-100 text-blue-700',
  state:    'bg-teal-100 text-teal-700',
  discom:   'bg-amber-100 text-amber-700',
  consumer: 'bg-green-100 text-green-700',
  vendor:   'bg-gray-100 text-gray-700',
};

/* ══════════════════════════════════════════════════════════════════════════
   ADD USER MODAL
   ══════════════════════════════════════════════════════════════════════════ */
interface AddUserModalProps {
  onClose:  () => void;
  onAdded:  (result: AddUserResponse) => void;
}

function AddUserModal({ onClose, onAdded }: AddUserModalProps) {
  const [displayName, setDisplayName] = useState('');
  const [username,    setUsername]    = useState('');
  const [password,    setPassword]    = useState('');
  const [showPwd,     setShowPwd]     = useState(false);
  const [role,        setRole]        = useState('national');
  const [org,         setOrg]         = useState('');
  const [scope,       setScope]       = useState('');
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    setLoading(true);
    try {
      const res = await axios.post<AddUserResponse>('/api/admin/users', {
        username: username.trim().toLowerCase(),
        password,
        displayName: displayName.trim(),
        role, org: org.trim(), scope: scope.trim(),
      }, { withCredentials: true });
      onAdded(res.data);
    } catch (err: unknown) {
      setError(axios.isAxiosError(err) ? (err.response?.data?.error || 'Failed to add user.') : 'Failed to add user.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-card border border-border shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h3 className="text-sm font-bold text-text-primary">Add New User</h3>
          <button onClick={onClose} className="text-text-tertiary hover:text-text-primary"><X size={16} /></button>
        </div>
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          {error && <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-danger">{error}</div>}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-text-secondary mb-1.5">Full Name</label>
              <input type="text" value={displayName} onChange={e => setDisplayName(e.target.value)} placeholder="Full name"
                className="w-full px-3 py-2 rounded-lg border border-border text-sm text-text-primary bg-bg-primary focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent" required />
            </div>
            <div>
              <label className="block text-xs font-medium text-text-secondary mb-1.5">Username</label>
              <input type="text" value={username} onChange={e => setUsername(e.target.value)} placeholder="username"
                className="w-full px-3 py-2 rounded-lg border border-border text-sm text-text-primary bg-bg-primary focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent" required />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1.5">Password</label>
            <div className="relative">
              <input type={showPwd ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)}
                placeholder="Min 8 characters"
                className="w-full px-3 py-2 pr-9 rounded-lg border border-border text-sm text-text-primary bg-bg-primary focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent" required />
              <button type="button" onClick={() => setShowPwd(p => !p)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-text-tertiary">
                {showPwd ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1.5">Role</label>
            <select value={role} onChange={e => setRole(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-border text-sm text-text-primary bg-bg-primary focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent">
              {ROLE_OPTIONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-text-secondary mb-1.5">Organisation</label>
              <input type="text" value={org} onChange={e => setOrg(e.target.value)} placeholder="e.g. GEDA"
                className="w-full px-3 py-2 rounded-lg border border-border text-sm text-text-primary bg-bg-primary focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent" />
            </div>
            <div>
              <label className="block text-xs font-medium text-text-secondary mb-1.5">Scope</label>
              <input type="text" value={scope} onChange={e => setScope(e.target.value)} placeholder="e.g. Gujarat"
                className="w-full px-3 py-2 rounded-lg border border-border text-sm text-text-primary bg-bg-primary focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent" />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose}
              className="px-4 py-2 text-xs text-text-secondary hover:text-text-primary border border-border rounded-lg hover:bg-bg-secondary transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={loading}
              className="px-4 py-2 text-xs bg-accent hover:bg-accent/90 disabled:opacity-60 text-white rounded-lg font-semibold transition-colors">
              {loading ? 'Creating…' : 'Create User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   EDIT USER MODAL — change role and/or password
   ══════════════════════════════════════════════════════════════════════════ */
interface EditUserModalProps {
  user:    AdminUser;
  onClose: () => void;
  onSaved: (updated: AdminUser) => void;
}

function EditUserModal({ user, onClose, onSaved }: EditUserModalProps) {
  const [role,       setRole]       = useState(user.role);
  const [password,   setPassword]   = useState('');
  const [showPwd,    setShowPwd]    = useState(false);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (password && password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (role === user.role && !password) { setError('No changes made.'); return; }

    setLoading(true);
    try {
      const body: Record<string, string> = { role };
      if (password) body.password = password;

      const res = await axios.put<{ success: boolean; user: AdminUser }>(
        `/api/admin/users/${user.id}`, body, { withCredentials: true }
      );
      onSaved(res.data.user);
    } catch (err: unknown) {
      setError(axios.isAxiosError(err) ? (err.response?.data?.error || 'Failed to save.') : 'Failed to save.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-card border border-border shadow-xl w-full max-w-sm">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div>
            <h3 className="text-sm font-bold text-text-primary">Edit User</h3>
            <p className="text-xs text-text-tertiary mt-0.5">@{user.username} · {user.displayName}</p>
          </div>
          <button onClick={onClose} className="text-text-tertiary hover:text-text-primary"><X size={16} /></button>
        </div>
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          {error && <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-danger">{error}</div>}

          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1.5">Role</label>
            <select value={role} onChange={e => setRole(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-border text-sm text-text-primary bg-bg-primary focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent">
              {ROLE_OPTIONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1.5">
              New Password
              <span className="text-text-tertiary font-normal ml-1">(leave blank to keep existing)</span>
            </label>
            <div className="relative">
              <input type={showPwd ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)}
                placeholder="Enter new password"
                className="w-full px-3 py-2 pr-9 rounded-lg border border-border text-sm text-text-primary bg-bg-primary focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent" />
              <button type="button" onClick={() => setShowPwd(p => !p)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-text-tertiary">
                {showPwd ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onClose}
              className="px-4 py-2 text-xs text-text-secondary hover:text-text-primary border border-border rounded-lg hover:bg-bg-secondary transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={loading}
              className="px-4 py-2 text-xs bg-accent hover:bg-accent/90 disabled:opacity-60 text-white rounded-lg font-semibold transition-colors">
              {loading ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   QR CODE MODAL
   ══════════════════════════════════════════════════════════════════════════ */
interface QRModalProps {
  result:  AddUserResponse;
  onClose: () => void;
}

function QRModal({ result, onClose }: QRModalProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-card border border-border shadow-xl w-full max-w-sm text-center">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h3 className="text-sm font-bold text-text-primary">Set Up 2FA for New User</h3>
          <button onClick={onClose} className="text-text-tertiary hover:text-text-primary"><X size={16} /></button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <p className="text-xs text-text-secondary">
            Share this QR code with <span className="font-semibold text-text-primary">{result.user.displayName}</span> to configure their authenticator app.
          </p>
          <div className="flex justify-center">
            <img src={result.qrCode} alt="TOTP QR Code" className="w-44 h-44 border border-border rounded-lg p-2" />
          </div>
          <div className="bg-bg-secondary rounded-lg px-4 py-3 text-left">
            <p className="text-xs text-text-secondary font-semibold mb-1">Manual key:</p>
            <p className="font-mono text-xs text-text-primary break-all select-all">{result.totpSecret}</p>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-left">
            <p className="text-xs text-warning font-medium">⚠ One-time display only</p>
            <p className="text-xs text-text-secondary mt-0.5">This QR code will not appear again. Save or screenshot it now.</p>
          </div>
          <button onClick={onClose} className="w-full py-2.5 bg-success hover:bg-success/90 text-white text-xs font-semibold rounded-lg transition-colors">
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   MAIN PAGE
   ══════════════════════════════════════════════════════════════════════════ */
export default function AdminUsersPage() {
  const [users,      setUsers]      = useState<AdminUser[]>([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState('');
  const [showAdd,    setShowAdd]    = useState(false);
  const [qrResult,   setQrResult]   = useState<AddUserResponse | null>(null);
  const [editUser,   setEditUser]   = useState<AdminUser | null>(null);
  const [deleting,   setDeleting]   = useState<string | null>(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get<{ users: AdminUser[] }>('/api/admin/users', { withCredentials: true });
      setUsers(res.data.users);
    } catch {
      setError('Failed to load users.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleUserAdded = (result: AddUserResponse) => {
    setShowAdd(false);
    setQrResult(result);
    fetchUsers();
  };

  const handleUserEdited = (updated: AdminUser) => {
    setUsers(prev => prev.map(u => u.id === updated.id ? updated : u));
    setEditUser(null);
  };

  const handleDelete = async (id: string, name: string) => {
    if (!window.confirm(`Delete user "${name}"? This cannot be undone.`)) return;
    setDeleting(id);
    try {
      await axios.delete(`/api/admin/users/${id}`, { withCredentials: true });
      setUsers(prev => prev.filter(u => u.id !== id));
    } catch {
      alert('Failed to delete user.');
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* KPI row */}
      <div className="flex gap-4">
        <div className="bg-white rounded-card border border-border shadow-card p-4 flex-1">
          <p className="text-xs text-text-secondary font-semibold uppercase tracking-wide">Total Users</p>
          <p className="text-2xl font-bold text-text-primary mt-1">{users.length}</p>
          <p className="text-xs text-text-secondary mt-1">Registered accounts</p>
        </div>
        <div className="bg-white rounded-card border border-border shadow-card p-4 flex-1">
          <p className="text-xs text-text-secondary font-semibold uppercase tracking-wide">Admins</p>
          <p className="text-2xl font-bold text-navy mt-1">{users.filter(u => u.role === 'admin').length}</p>
          <p className="text-xs text-text-secondary mt-1">Administrator accounts</p>
        </div>
        <div className="bg-white rounded-card border border-border shadow-card p-4 flex-1">
          <p className="text-xs text-text-secondary font-semibold uppercase tracking-wide">Roles Active</p>
          <p className="text-2xl font-bold text-accent mt-1">{new Set(users.map(u => u.role)).size}</p>
          <p className="text-xs text-text-secondary mt-1">Distinct role types</p>
        </div>
        <div className="bg-white rounded-card border border-border shadow-card p-4 flex-1 flex items-center justify-center">
          <button onClick={() => setShowAdd(true)}
            className="flex items-center gap-2 px-4 py-2.5 bg-accent hover:bg-accent/90 text-white text-xs font-semibold rounded-lg transition-colors">
            <Plus size={14} />
            Add User
          </button>
        </div>
      </div>

      {/* User table */}
      <div className="bg-white rounded-card border border-border shadow-card p-4">
        <div className="flex items-center justify-between mb-4">
          <SectionDivider title="User Accounts" subtitle="All registered platform users" />
          <button onClick={fetchUsers}
            className="flex items-center gap-1.5 text-xs text-text-tertiary hover:text-text-primary transition-colors">
            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-danger mb-4">{error}</div>
        )}

        {loading && !users.length ? (
          <div className="py-12 text-center text-sm text-text-tertiary">Loading users…</div>
        ) : users.length === 0 ? (
          <div className="py-12 text-center">
            <Users size={32} className="text-text-tertiary mx-auto mb-3" />
            <p className="text-sm text-text-tertiary">No users yet. Add one to get started.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-2.5 text-text-secondary font-semibold">Name</th>
                  <th className="text-left py-2.5 text-text-secondary font-semibold">Username</th>
                  <th className="text-left py-2.5 text-text-secondary font-semibold">Role</th>
                  <th className="text-left py-2.5 text-text-secondary font-semibold">Organisation</th>
                  <th className="text-left py-2.5 text-text-secondary font-semibold">Scope</th>
                  <th className="text-left py-2.5 text-text-secondary font-semibold">Created</th>
                  <th className="text-center py-2.5 text-text-secondary font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id} className="border-b border-border/50 hover:bg-bg-secondary transition-colors">
                    <td className="py-3 font-medium text-text-primary">{u.displayName}</td>
                    <td className="py-3 text-text-secondary font-mono">@{u.username}</td>
                    <td className="py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium capitalize ${ROLE_BADGE[u.role] ?? 'bg-gray-100 text-gray-600'}`}>
                        {u.role}
                      </span>
                    </td>
                    <td className="py-3 text-text-secondary">{u.org || '—'}</td>
                    <td className="py-3 text-text-secondary">{u.scope || '—'}</td>
                    <td className="py-3 text-text-tertiary">
                      {new Date(u.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </td>
                    <td className="py-3">
                      <div className="flex items-center justify-center gap-2">
                        {/* Edit */}
                        <button
                          onClick={() => setEditUser(u)}
                          className="text-text-tertiary hover:text-accent transition-colors"
                          title="Edit user"
                        >
                          <Pencil size={14} />
                        </button>
                        {/* Delete */}
                        <button
                          onClick={() => handleDelete(u.id, u.displayName)}
                          disabled={deleting === u.id}
                          className="text-text-tertiary hover:text-danger disabled:opacity-40 transition-colors"
                          title="Delete user"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modals */}
      {showAdd  && <AddUserModal  onClose={() => setShowAdd(false)} onAdded={handleUserAdded} />}
      {editUser && <EditUserModal user={editUser} onClose={() => setEditUser(null)} onSaved={handleUserEdited} />}
      {qrResult && <QRModal result={qrResult} onClose={() => setQrResult(null)} />}
    </div>
  );
}
